import { tracked } from '@glimmer/tracking'

/**
 * Utility for handling asynchronous values with tracking in Ember/Glimmer components.
 *
 * This module provides a wrapper around Promise-based operations that tracks loading state,
 * resolved values, and errors. It's designed to work with Ember's reactivity system to
 * automatically update templates when async states change.
 *
 * This class is used in conjunction with the `@trackedAsync` decorator. The decorated property
 * is transformed into an instance of `Awaitable`.
 */
export class Awaitable<T> {
  @tracked value: T | null | undefined
  @tracked loading = false
  @tracked error: unknown = null

  private _promise: Promise<T | undefined> | null = null

  constructor(initial: T | Promise<T | undefined> | null = null) {
    if (initial) {
      this.promise = initial
    }
  }

  /**
   * Sets a new value or promise to be tracked.
   *
   * If a promise is provided, it will update the loading state and handle
   * resolution/rejection appropriately. If a direct value is provided,
   * it will be set immediately with no loading state.
   *
   * @param input The value or promise to set
   */
  set promise(input: T | Promise<T | undefined>) {
    if (input instanceof Promise) {
      this.loading = true
      this.error = null
      this._promise = input

      input
        .then(
          (resolved) => {
            if (this._promise === input) {
              this.value = resolved
            }
          },
          (e) => {
            if (this._promise === input) {
              this.error = e
            }
          }
        )
        .finally(() => {
          if (this._promise === input) {
            this.loading = false
          }
        })
    } else {
      this._promise = null
      this.value = input
      this.loading = false
      this.error = null
    }
  }

  /**
   * Gets the underlying promise being tracked.
   *
   * If there's an active promise, returns it. Otherwise, returns a resolved
   * promise with the current value (which may be null).
   *
   * @returns A promise that resolves to the current value
   */
  get promise(): Promise<T | undefined | null> {
    if (this._promise) {
      return this._promise
    }
    return Promise.resolve(this.value)
  }

  get resolved(): boolean {
    return !this.loading && this.error === null
  }

  get rejected(): boolean {
    return this.error !== null
  }

  reset(): void {
    this.value = undefined
    this.error = null
    this.loading = false
    this._promise = null
  }
}
